import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import {
  FormValidationSubmitService,
  SubmitForValidationResult,
} from '../../../services/mbrs/form-validation-submit.service';
import { XbrlDataTransformerService } from '../../../services/mbrs/xbrl-data-transformer.service';
import {
  FilingFormState,
  FilingFormStateService,
} from '../../../services/mbrs/filing-form-state.service';
import { ConversionValidationCacheService } from '../../../services/mbrs/conversion-validation-cache.service';
import { FilingFormPersistenceService } from '../../../services/mbrs/filing-form-persistence.service';
import { VisibleProjectionRegistry } from '../../../services/mbrs/visible-projection-registry.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DEI, SSMT } from '../../../constants/qnames';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { buildStateKeyToSlug } from '../../../models/sheet-descriptors';
import { TOAST_DURATION_LONG_MS } from '../../../utils/toast-durations.util';
import { conversionActionFailureCopy } from '../../../utils/conversion-error-codes.util';
import { InteractiveToastComponent } from '../interactive-toast/interactive-toast.component';

/**
 * Per-sheet status flags sent in the submit-for-validation request body. The
 * request map changed from `{ slug: boolean }` to `{ slug: SubmitSheetStatus }`
 * — the backend now derives each sheet's stored `MbrsStatusEnum` (and the
 * conversion's roll-up) from these two flags it used to receive pre-collapsed:
 *   hasErrors                          → Review (3)
 *   !hasErrors && hasSuggestedChanges  → SuccessWithWarnings (6)
 *   !hasErrors && !hasSuggestedChanges → Success (1)
 * A populated sheet with NO entry is treated as Review (3) by the backend, so
 * `ensureCompleteDtosOrDemoteStatuses` still excludes out-of-scope sheets.
 */
export interface SubmitSheetStatus {
  hasErrors: boolean;
  hasSuggestedChanges: boolean;
}

@Component({
  selector: 'submit-for-validation',
  imports: [
    ButtonModule,
    CommonModule,
    ConfirmDialog,
    InteractiveToastComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './submit-for-validation.component.html',
  styleUrl: './submit-for-validation.component.css',
})
export class SubmitForValidationComponent {
  /**
   * Mapping from FE state-service keys to the BE sheet slugs sent in the
   * submit payload. Only the 19 entries here are eligible to appear in
   * `sheetStatuses`; alternative format slots (e.g. SocfDirectMethod) are
   * tracked in state but never submitted under the current form path.
   *
   * Used by:
   *   - buildSheetStatuses (forward: stateKey → slug)
   *   - ensureCompleteDtosOrDemoteStatuses (forward, but indexed by slug)
   *   - countTrackedValidationErrors (forward, to bound the error count to
   *     sheets that actually appear in the payload)
   */
  private static readonly STATE_KEY_TO_SLUG: Record<string, string> =
    buildStateKeyToSlug();

  private confirmationService = inject(ConfirmationService);

  // Inputs from parent component
  @Input() form!: FormGroup;
  @Input() sheetKey!: keyof FilingFormState;
  @Input() formTitle!: string;
  @Input() visibleFormData?: any; // Optional: Pass only visible form data
  @Input() customValidation?: () => boolean; // Optional: Custom validation callback
  @Input() evaluateSheetStatus?: () => void; // Optional: Re-evaluate sheet status from mapping status before submit
  @Input() externallyDisabled: boolean = false; // Optional: External gate (e.g. reload-prompt-dismissed) forcing submit-disabled.
  @Input() externallyDisabledReason?: string; // Optional: amber inline message rendered below submit when externallyDisabled.
  @Output() cancelConfirmed = new EventEmitter<void>(); // Emitted when cancel is confirmed (revert completed)

  // Services
  private validationSubmitService = inject(FormValidationSubmitService);
  private xbrlTransformer = inject(XbrlDataTransformerService);
  private messageService = inject(MessageService);
  private stateService = inject(FilingFormStateService);
  private validationCache = inject(ConversionValidationCacheService);
  private persistence = inject(FilingFormPersistenceService);
  private visibleProjectionRegistry = inject(VisibleProjectionRegistry);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);

  // Component state
  isSubmitting = false;

  /**
   * Submit form data for validation
   * Submits data to backend, then redirects to XBRL File page with toast notification
   */
  async onSubmit(): Promise<void> {
    try {
      this.isSubmitting = true;
      this.cdr.detectChanges();

      // Re-evaluate sheet status from mapping status before building payload
      this.evaluateSheetStatus?.();

      // Small delay to ensure the loading spinner is visible
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Get the file name from route params (used for the post-submit toast
      // wording on the listing page).
      const fileName =
        this.route.snapshot.queryParamMap.get('fileName') || 'Unknown File';
      const leadingFileName = fileName.replace(/\.[^/.]+$/, '');

      // Get conversionId from state service
      const conversionId = this.stateService.getConversionId();

      // Retrieve ALL data from the persisted modified-state blob
      // (FilingFormPersistenceService / IndexedDB). The returned object is
      // already deserialized — same blob shape the editor persists — so no
      // JSON.parse is needed. A missing blob falls through to the implicit
      // no-data return below, exactly as a missing localStorage key did.
      const allSheetsData: any =
        await this.persistence.getModified(conversionId);

      if (allSheetsData) {
        // Build sheetStatuses first, then ensure every sheet flagged `true`
        // carries a complete DTO. Per the new submit-for-validation contract, the BE
        // does a full-replace of the stored sheet — a partial DTO with a
        // `true` status would wipe the omitted fields. The fallback either
        // backfills from the API snapshot (originalState) or demotes the
        // slug to `false` so the BE skips the sheet entirely. Must run BEFORE
        // transformations so backfilled data goes through the same FE→BE
        // conversions as edited data.
        const sheetStatuses = this.buildSheetStatuses();
        this.ensureCompleteDtosOrDemoteStatuses(allSheetsData, sheetStatuses);
        this.dropInactiveFilingInfoSource(allSheetsData);

        // Strip conditionally-hidden fields from each registered sheet's slice
        // BEFORE the FE→BE transforms, so orphaned hidden-field values (backend
        // hydration, or a parent selector the user later changed) are never
        // submitted as XBRL facts. Runs after the DTO-completeness backfill —
        // which only acts on EMPTY sheets — so removing a few keys here can't
        // re-trip it. Sheets without a registered projector are untouched.
        // See VisibleProjectionRegistry (rolled out one sheet at a time).
        this.visibleProjectionRegistry.projectAll(allSheetsData, {
          filingInfo: this.stateService.getActiveFilingInfoData() ?? {},
          allSheets: allSheetsData,
        });

        // Transform SCOPE_OF_FILING data
        if (allSheetsData[SHEET_KEYS.scopeOfFiling]) {
          const transformedScopeData =
            this.xbrlTransformer.transformBusinessMembersToBackend(
              allSheetsData[SHEET_KEYS.scopeOfFiling],
            );
          allSheetsData[SHEET_KEYS.scopeOfFiling] = transformedScopeData;
        }

        const transformationMap: Record<string, (data: any) => any> = {
          statementOfFinancialPosition: (data) =>
            this.xbrlTransformer.transformStatementDimensionDataToBackend(data),
          subClassificationOfAssetsLiabilitiesEquity: (data) =>
            this.xbrlTransformer.transformStatementDimensionDataToBackend(data),
          statementOfProfitOrLoss: (data) =>
            this.xbrlTransformer.transformStatementStringToBackend(data),
          analysisOfProfitOrLoss: (data) =>
            this.xbrlTransformer.transformStatementStringToBackend(data),
          statementOfComprehensiveIncome: (data) =>
            this.xbrlTransformer.transformStatementStringToBackend(data),
          statementOfCashFlowsIndirectMethod: (data) =>
            this.xbrlTransformer.transformStatementStringToBackend(data),
          statementOfChangesInEquity: (data) =>
            this.xbrlTransformer.transformUnderscoreFormatToApiDimensional(
              data,
            ),
          notesIssuedCapital: (data) =>
            this.xbrlTransformer.transformNotesIssuedCapitalToBackend(data),
          notesRelatedPartyTransactions: (data) =>
            this.xbrlTransformer.transformNotesRelatedPartyTransactionsToBackend(
              data,
            ),
        };

        Object.keys(transformationMap).forEach((key) => {
          if (allSheetsData[key]) {
            allSheetsData[key] = transformationMap[key](allSheetsData[key]);
          }
        });

        // The BE no longer recomputes pass/fail or counts errors — the
        // frontend is now the validation authority. The two new fields are
        // persisted to MbrsDataLog and drive the `returnStatus` echoed back
        // in the response. Count is bound to the 19 tracked sheets so that
        // alternative-format slots (default hasErrors: true) don't inflate
        // it. The two values must be consistent: count === 0 ⇔ status true.
        const frontendValidationErrorCount =
          this.countFailingSheetStatuses(sheetStatuses);
        const frontendValidationStatus = frontendValidationErrorCount === 0;

        const completePayload = {
          ...allSheetsData,
          sheetStatuses,
          frontendValidationStatus,
          frontendValidationErrorCount,
        };

        // Submit to backend. The result is a discriminated union:
        //   - 'success'          : HTTP 200, body.status true, persisted, validation passed.
        //   - 'validation-failed': HTTP 200, body.status false, persisted, has errors.
        //   - 'error'            : transport/infrastructure fault, NOT persisted.
        const result: SubmitForValidationResult =
          await this.validationSubmitService.submitForValidationWithData(
            this.form,
            completePayload,
            this.sheetKey,
          );

        if (result.tag === 'error') {
          // Transport / server fault (HTTP 4xx/5xx, timeout, network error,
          // or empty payload). Backend did NOT persist — keep the user on
          // the form so they can retry without losing local changes.
          this.messageService.clear();
          // UI parity with the delete error toast: render through the shared
          // `interactive-toast` (`custom-error` severity → red styling) via this
          // component's OWN keyed outlet, so it shows regardless of which host
          // MessageService instance is in scope. Message TEXT is intentionally
          // unchanged (summary "Error" + detail below).
          this.messageService.add({
            key: 'submit_for_validation_error',
            severity: 'custom-error',
            summary:
              conversionActionFailureCopy(result.errorCode) ||
              'Failed to submit for validation. Please try again later.',
            life: TOAST_DURATION_LONG_MS,
          });
          this.isSubmitting = false;
          return;
        }

        // Both 'success' and 'validation-failed' mean the backend persisted
        // the payload. Commit local state, invalidate cache, notify subscribers,
        // then redirect — only the toast differs.
        this.stateService.commitModifiedStateAsOriginal();
        this.validationCache.invalidate(conversionId);
        // Note: per-sheet "Edited" Mapping Status is now derived from the
        // last-submission baseline (FilingFormStateService.originalState, refreshed
        // by commitModifiedStateAsOriginal above) via MappingStatusService — there
        // are no sessionStorage edited-markers left to clear here.
        this.stateService.notifySubmissionCommitted(
          [
            this.stateService.getActiveFilingInfoKey(),
            SHEET_KEYS.scopeOfFiling,
          ],
          this.sheetKey,
        );

        // Cross-tab resubmit safeguard: tell OTHER tabs on this conversion to
        // leave the form and return to the listing. The conversion has just
        // been committed and re-enters backend validation, so a sibling tab
        // sitting on the now-stale sheet data must not keep editing it. Reuses
        // the same immediate 'conversionReset' broadcast as cancel/revert; the
        // container redirects matching tabs and tags that navigation with
        // CROSS_TAB_RESET_NAV_STATE, so canDeactivateFilingGuard skips the
        // "unsaved changes" prompt on the passive tabs. The self-echo filter
        // excludes this (submitting) tab, which navigates below regardless.
        this.stateService.requestConversionResetAcrossTabs();

        // Toast strategy (AS-842):
        //  - `toast` is the *transient* "we received it, validating" message,
        //    shown immediately on landing for BOTH outcomes. It must NOT carry
        //    terminal wording — the listing page owns the terminal toast.
        //  - `pendingValidation` tells the listing which conversion was just
        //    submitted so it fires exactly ONE terminal toast for that row
        //    (XbrlFilesComponent.ngAfterViewInit -> fireTerminalToast, or the
        //    resolver path if the row arrives still PendingValidation).
        //
        // Previously the `validation-failed` branch put the *terminal* "fields
        // could be incorrect" wording in `toast`, which then duplicated the
        // listing's own terminal toast — so it appeared twice on re-submit.
        // Keeping `toast` transient-only removes that duplicate while the
        // listing still surfaces the terminal result once.
        const toast = {
          key: 'process_mbrs_data',
          severity: 'custom-warning',
          summary:
            "We are validating the fields updated and will notify you when it's ready.",
          life: TOAST_DURATION_LONG_MS,
        };

        this.router.navigate(['/mbrs/xbrl-files'], {
          state: {
            toast,
            pendingValidation: {
              fileName: leadingFileName,
              conversionId: this.stateService.getConversionId(),
            },
          },
        });
        return;
      }

      // No persisted modified blob: nothing was sent. Stop the spinner so the
      // button does not stay busy forever.
      // Developer Note (TODO): the user still gets no message here. Whether to
      // show one (and its wording) is for the P.O. to decide.
      this.isSubmitting = false;
    } catch (error) {
      console.error('❌ Error in onSubmit:', error);
      this.isSubmitting = false;
    }
  }

  /**
   * Handle cancel action - show confirmation dialog if there are unsaved changes
   * If confirmed, revert form to initially mapped values
   * IMPORTANT: Converts date strings back to Date objects for PrimeNG DatePicker
   * After reverting, the submit button will be disabled since there are no unsaved changes
   */
  onCancel(): void {
    // If no changes, do nothing
    if (!this.isModified()) {
      return;
    }

    // If there are changes, show confirmation dialog
    this.confirmationService.confirm({
      header: 'Cancel Changes',
      message: 'Any unsaved changes will be lost. Do you want to continue?',
      acceptLabel: 'Confirm',
      rejectLabel: 'Cancel',
      closable: false,
      acceptButtonStyleClass:
        '!min-w-[80px] xlplus:!min-w-[100px] !text-sm xlplus:!text-base !bg-taxpod-blue !border-taxpod-blue !font-lato !font-bold',
      rejectButtonStyleClass:
        '!min-w-[80px] xlplus:!min-w-[100px] !border-taxpod-blue !text-taxpod-blue !border-[2px] !text-sm xlplus:!text-base hover:!bg-[#E0F2FE] !bg-white',
      accept: () => {
        this.revertToInitialValues();
        this.cancelConfirmed.emit();
        // Guarantee the per-conversion validation cache is gone after Cancel.
        // Invalidates now AND suppresses the sheet's trailing post-cancel
        // re-baseline write (which fires ~1s later, after the navigation-away
        // wipe) so it cannot re-create mbrs-validation-cache-<id>. The
        // suppression auto-clears on the next form entry (setConversionId).
        this.stateService.abandonValidationCache(
          this.stateService.getConversionId(),
        );
        this.router.navigate(['/mbrs/xbrl-files']);
      },
      reject: () => {
        // Do nothing, stay on page
      },
    });
  }

  /**
   * Revert form to initially mapped values
   * Called after user confirms cancel action
   * Reverts ALL sheets (not just current) since Submit checks hasAnySheetModified()
   */
  private revertToInitialValues(): void {
    // Revert ALL sheets to original state (not just current sheet)
    // This is necessary because the Submit button is enabled based on hasAnySheetModified()
    // which checks all sheets, so Cancel must also revert all sheets
    this.stateService.cancelChanges();

    // Get reverted state for the current sheet
    const afterData = this.stateService.getSheetData(this.sheetKey);

    if (this.form) {
      // First: Reset ALL form controls to default values to ensure no stale values remain
      // This handles controls that exist in the form but not in the original data
      // For calculated/total fields: use 0
      // For non-calculated numeric fields: use null (preserve NULL for unmapped API data)
      // For non-numeric fields: use null
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        if (control) {
          // Only calculated/total fields should default to 0
          // Non-calculated numeric fields should remain as null
          const defaultValue = this.isCalculatedTotalField(key) ? 0 : null;
          control.setValue(defaultValue, { emitEvent: false });
        }
      });

      // Then: Patch with original data values (if any)
      if (afterData && Object.keys(afterData).length > 0) {
        Object.keys(afterData).forEach((key) => {
          const control = this.form.get(key);
          if (control) {
            let newValue = afterData[key];

            // Convert date strings to Date objects for PrimeNG DatePicker
            if (
              this.isDateField(key) &&
              newValue &&
              typeof newValue === 'string'
            ) {
              newValue = new Date(newValue);
            }

            // ONLY convert null/undefined/empty to 0 for calculated/total fields
            // Non-calculated fields should remain as null (preserve NULL for unmapped API data)
            if (
              this.isCalculatedTotalField(key) &&
              (newValue === null || newValue === undefined || newValue === '')
            ) {
              newValue = 0;
            }

            control.setValue(newValue, { emitEvent: false });
          }
        });
      }

      // Mark all controls as pristine and untouched after reverting
      // This ensures a clean state without error messages showing
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        if (control) {
          control.markAsPristine();
          control.markAsUntouched();
        }
      });

      // Re-validate all controls but do NOT mark them as touched
      // Use emitEvent: false to prevent triggering valueChanges subscriptions
      // that could overwrite the reverted state with incomplete data (disabled controls excluded)
      this.form.updateValueAndValidity({ emitEvent: false });
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        if (control) {
          control.updateValueAndValidity({ emitEvent: false });
        }
      });
    }
  }

  /**
   * Check if a field is a date field based on field name
   * Returns true if field name contains 'Date' or is a known date field
   */
  private isDateField(fieldName: string): boolean {
    // Check if field name contains 'Date'
    if (fieldName.includes('Date')) {
      return true;
    }

    // Add specific known date fields here if needed
    const dateFields: string[] = [
      SSMT.DateOfSigningDirectorsReport,
      DEI.CompanyCurrentFinancialYearStartDate,
      DEI.CompanyCurrentFinancialYearEndDate,
      DEI.CompanyPreviousFinancialYearStartDate,
      DEI.CompanyPreviousFinancialYearEndDate,
      DEI.StatutoryDeclarationForRectification,
      // Add more date fields as needed
    ];

    return dateFields.includes(fieldName);
  }

  /**
   * Check if a field is a numeric field that should default to 0 instead of null
   * Returns true for XBRL monetary/numeric fields and fields not identified as dates or text
   */
  private isNumericField(fieldName: string): boolean {
    // Skip date fields
    if (this.isDateField(fieldName)) {
      return false;
    }

    // Known text/non-numeric field patterns
    const nonNumericPatterns = [
      'Name',
      'Description',
      'Text',
      'String',
      'Address',
      'Email',
      'Phone',
      'Fax',
      'Country',
      'State',
      'City',
      'PostalCode',
      'RegistrationNumber',
      'Identifier',
      'Code',
      'Type',
      'Status',
      'Comment',
      'Note',
      'Remark',
      'Reason',
      'Explanation',
      'Boolean',
      'Flag',
      'Indicator',
    ];

    // Check if field name contains any non-numeric pattern
    for (const pattern of nonNumericPatterns) {
      if (fieldName.includes(pattern)) {
        return false;
      }
    }

    // XBRL monetary/numeric field patterns (common in financial statements)
    const numericPatterns = [
      'Amount',
      'Value',
      'Balance',
      'Total',
      'Sum',
      'Assets',
      'Liabilities',
      'Equity',
      'Revenue',
      'Expense',
      'Income',
      'Loss',
      'Profit',
      'Cost',
      'Price',
      'Cash',
      'Receivable',
      'Payable',
      'Capital',
      'Reserve',
      'Dividend',
      'Interest',
      'Tax',
      'Depreciation',
      'Amortization',
      'mpers:',
      'mfrs:',
      'ssmt:', // XBRL namespace prefixes for financial data
    ];

    // Check if field name contains any numeric pattern
    for (const pattern of numericPatterns) {
      if (fieldName.includes(pattern)) {
        return true;
      }
    }

    // Default to numeric for unrecognized XBRL fields (most are monetary values)
    return fieldName.includes(':');
  }

  /**
   * Check if a field is a calculated/total field
   * Calculated/total fields should default to 0 and be converted to 0 when null
   * Returns true for fields that contain Total, Net, Gross, Sum patterns
   * These are typically auto-calculated fields that aggregate other values
   */
  private isCalculatedTotalField(fieldName: string): boolean {
    // Skip date and text fields - they are never calculated
    if (this.isDateField(fieldName) || !this.isNumericField(fieldName)) {
      return false;
    }

    // Patterns that indicate calculated/total fields
    // These fields should default to 0 and be converted to 0 when null
    const calculatedPatterns = [
      'Total', // TotalAssets, TotalLiabilities, TotalEquity, etc.
      'NetCash', // NetCashFromOperating, NetCashUsedInInvesting, etc.
      'GrossProfit', // GrossProfit
      'ProfitLoss', // ProfitLoss, ProfitLossBeforeTax
      'ProfitFor', // ProfitForTheYear
      'ProfitBefore', // ProfitBeforeTax
      'ProfitFrom', // ProfitFromOperations
      'ComprehensiveIncome', // TotalComprehensiveIncome
      'CashAndCashEquivalentsAt', // CashAndCashEquivalentsAtEndOfYear
      'NetIncrease', // NetIncreaseDecreaseInCashAndCashEquivalents
    ];

    // Check if field name contains any calculated pattern
    for (const pattern of calculatedPatterns) {
      if (fieldName.includes(pattern)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if form is valid
   */
  get isFormValid(): boolean {
    return this.form?.valid ?? false;
  }

  /**
   * Check if any sheet has unsaved changes (modified data compared to original)
   * Used to enable/disable the submit button across all sheets
   */
  get hasUnsavedChanges(): boolean {
    return this.stateService.hasAnySheetModified();
  }

  /**
   * Check if form has unsaved modifications
   */
  private isModified(): boolean {
    return this.stateService.isSheetModified(this.sheetKey);
  }

  /**
   * Build per-sheet validation statuses mapped to backend sheet slugs.
   * This is sent alongside the payload so the backend can derive each MbrsData
   * record's `MbrsStatusEnum` Status (and the conversion roll-up) individually.
   *
   * Emits the two flags verbatim — `{ hasErrors, hasSuggestedChanges }` — rather
   * than the old pre-collapsed `!hasErrors` boolean, so the backend can resolve
   * the SuccessWithWarnings (6) outcome (no errors, ≥1 warning) that a single
   * boolean could not express. See {@link SubmitSheetStatus}.
   */
  private buildSheetStatuses(): Record<string, SubmitSheetStatus> {
    const allStatus = this.stateService.getAllSheetLevelStatus();
    const sheetStatuses: Record<string, SubmitSheetStatus> = {};
    for (const [stateKey, slug] of Object.entries(
      SubmitForValidationComponent.STATE_KEY_TO_SLUG,
    )) {
      const status = (allStatus as any)[stateKey];
      if (status) {
        sheetStatuses[slug] = {
          hasErrors: !!status.hasErrors,
          hasSuggestedChanges: !!status.hasSuggestedChanges,
        };
      }
    }
    return sheetStatuses;
  }

  /**
   * Drop the inactive filing-info source slot so its stub-default data does
   * not reach the wire.
   *
   * `filingInfo` (Phase 2 / role1) and `filingPrerequisites` (Phase 1 / role0)
   * map to **different** DTOs since the dual-DTO split. So
   * the original collision (both slots aliasing to `FilingInfoDto` and the
   * inactive slot clobbering the active one through insertion-order race) is
   * no longer possible structurally.
   *
   * This drop remains as **defense-in-depth**: even with separate DTOs,
   * `FilingFormStateService.applyDefaultValuesToState` unconditionally
   * force-sets a 16-key defaults dictionary into `filingPrerequisites` on
   * every state write (without the `if (qname in updatedSheet)` guard that
   * protects `filingInfo`). In Phase 2 that leaves the inactive
   * `filingPrerequisites` slot populated with stub defaults we have no reason
   * to ship — they would land on the wire as a 16-field
   * `FilingPrerequisitesDto` and risk being persisted by the BE as a Phase-1
   * submission for a Phase-2 conversion. Symmetric concern in Phase 1 for
   * `filingInfo`.
   *
   * The `getActiveFilingInfoKey()` rule (Phase 1 → `filingPrerequisites`,
   * Phase 2 → `filingInfo`) is the same authoritative-source rule that
   * `getActiveFilingInfoData()` already enforces elsewhere — keeping the
   * submission boundary aligned with it.
   */
  private dropInactiveFilingInfoSource(
    allSheetsData: Record<string, any>,
  ): void {
    const activeKey = this.stateService.getActiveFilingInfoKey();
    const inactiveKey =
      activeKey === SHEET_KEYS.filingInfo
        ? SHEET_KEYS.filingPrerequisites
        : SHEET_KEYS.filingInfo;
    delete allSheetsData[inactiveKey];
  }

  /**
   * For each sheet present in `sheetStatuses`, guarantee a complete DTO is
   * present in `allSheetsData`. If the modified-state slot is empty (the user
   * never visited the sheet, or its data drifted out of localStorage), fall
   * back to the original state (the API snapshot loaded on page entry). If both
   * sources are empty, EXCLUDE the slug from `sheetStatuses` (delete it) so the
   * BE skips the sheet rather than wiping its persisted fields.
   *
   * **Why exclude rather than keep:** the BE now treats a populated sheet with
   * no `sheetStatuses` entry as Review (3). An out-of-scope sheet (e.g.
   * InvolveInSE under MPERS) that is empty in both sources carries the default
   * `{ hasErrors: true }` and would otherwise roll the conversion into Review.
   * Deleting it (and emitting no DTO for it) keeps it out of the roll-up.
   *
   * **Why `present + partial` is unsafe:** the BE does a full-replace on submit.
   * A DTO missing keys is treated as the user clearing those fields, which is
   * what wiped Filing Info in the prior incident.
   */
  private ensureCompleteDtosOrDemoteStatuses(
    allSheetsData: Record<string, any>,
    sheetStatuses: Record<string, SubmitSheetStatus>,
  ): void {
    for (const [stateKey, slug] of Object.entries(
      SubmitForValidationComponent.STATE_KEY_TO_SLUG,
    )) {
      // Process EVERY present sheet regardless of its flags. The default
      // sheet-level status is `hasErrors: true`, so an unvalidated out-of-scope
      // sheet (e.g. InvolveInSE/stock-exchange under MPERS that was never
      // cleared) arrives here flagged `{ hasErrors: true }` and must STILL be
      // eligible for exclusion below — not skipped. Only skip sheets never
      // present at all.
      if (!(slug in sheetStatuses)) continue;

      const sheetData = allSheetsData[stateKey];
      const isEmpty =
        !sheetData ||
        (typeof sheetData === 'object' && Object.keys(sheetData).length === 0);

      if (!isEmpty) continue;

      const original = this.stateService.getOriginalSheetData(
        stateKey as keyof FilingFormState,
      );
      if (original && Object.keys(original).length > 0) {
        allSheetsData[stateKey] = { ...original };
      } else {
        // Out-of-scope sheet: empty in BOTH modified and original state (e.g.
        // InvolveInSE / stock-exchange under MPERS, which is never a visible
        // sheet there). EXCLUDE it from sheetStatuses entirely rather than
        // leaving its default `{ hasErrors: true }`. The backend rolls the
        // conversion to the worst sheet (Review > SuccessWithWarnings > Success)
        // and treats a present-but-unlisted sheet as Review, so a stray
        // `hasErrors: true` for a non-applicable sheet would wrongly pin the
        // conversion in Review. With no DTO emitted for this sheet either, the
        // BE skips it — preserving this branch's original anti-wipe intent (a
        // present + partial DTO would wipe persisted fields) without poisoning
        // the status roll-up. See submit-for-validation.component.spec.ts.
        delete sheetStatuses[slug];
      }
    }
  }

  /**
   * Count the sheets that report `hasErrors: true` among the 19 tracked
   * (submittable) sheets. Backs `frontendValidationErrorCount` in the payload.
   * Alternative-format slots (e.g. SocfDirectMethod) are excluded because their
   * default `hasErrors: true` would otherwise inflate the count for filings
   * that don't include them.
   */
  private countFailingSheetStatuses(
    sheetStatuses: Record<string, SubmitSheetStatus>,
  ): number {
    // Derived from the FINAL, cleaned `sheetStatuses` map — the same map the
    // backend rolls up (Review > SuccessWithWarnings > Success). Counting here
    // (rather than re-scanning all 19 tracked sheets) keeps
    // `frontendValidationErrorCount` consistent with the dict and ensures
    // out-of-scope sheets excluded by `ensureCompleteDtosOrDemoteStatuses` are
    // not counted. A warnings-only sheet (hasErrors:false) is NOT a failure, so
    // only `hasErrors` counts. Invariant: count === 0 ⇔ no submitted sheet has
    // errors (the conversion is Success or SuccessWithWarnings, never Review).
    return Object.values(sheetStatuses).filter((sheetStatus) => sheetStatus.hasErrors).length;
  }
}
